import * as React from "react";
import { Branch, Currency, XtrfService } from "gen/clients";
import Drawer from "@mui/material/Drawer";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import { Form, Formik } from "formik";
import { TextInputField } from "components/formikFields/TextInputField/TextInputField";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import * as Yup from "yup";
import { DrawerActions } from "components/DrawerActions/DrawerActions";
import { SELECT_OPTION_COMPARATOR } from "utils/stringUtils";

interface Props {
  onClose: () => void;
  onSave: (branch: Branch) => void;
  existingBranches: Branch[];
  xtrfServices: XtrfService[];
}

interface FormValues {
  id: string;
  name: string;
  xtrfId: string;
  xtrfServiceId: string;
  currency: string;
  supportEmail: string;
  phoneNumber: string;
  websiteUrl: string;
}

const validationSchema = Yup.object().shape({
  id: Yup.string().required("Branch ID is required").trim(),
  name: Yup.string().required("Branch name is required"),
  xtrfId: Yup.number()
    .typeError("XTRF ID must be a number")
    .required("XTRF ID is required")
    .positive("XTRF ID must be a positive number")
    .integer("XTRF ID must be an integer"),
  currency: Yup.string().required("Currency is required"),
  supportEmail: Yup.string().email("Invalid email address").notRequired(),
  phoneNumber: Yup.string().notRequired(),
  websiteUrl: Yup.string().url("Invalid URL format").notRequired()
});

const AddBranchPanel: React.FC<Props> = ({ onClose, onSave, existingBranches, xtrfServices }) => {
  const initialValues: FormValues = {
    id: "",
    name: "",
    xtrfId: "",
    xtrfServiceId: "",
    currency: "",
    supportEmail: "",
    phoneNumber: "",
    websiteUrl: ""
  };

  const xtrfServiceOptions = React.useMemo(
    () => [
      { value: "", label: "None" },
      ...xtrfServices
        .filter(service => service.active)
        .map(service => ({
          value: service.id.toString(),
          label: service.name
        }))
        .sort(SELECT_OPTION_COMPARATOR)
    ],
    [xtrfServices]
  );

  const currencyOptions = React.useMemo(
    () => [
      { value: "", label: "Select currency" },
      { value: "USD", label: "USD - US Dollar" },
      { value: "EUR", label: "EUR - Euro" },
      { value: "GBP", label: "GBP - British Pound" }
    ],
    []
  );

  const handleSubmit = (values: FormValues) => {
    onSave({
      id: values.id.trim(),
      name: values.name,
      xtrfId: Number(values.xtrfId),
      xtrfServiceId: values.xtrfServiceId ? Number(values.xtrfServiceId) : undefined,
      currency: values.currency as Currency,
      supportEmail: values.supportEmail.trim() || undefined,
      phoneNumber: values.phoneNumber.trim() || undefined,
      websiteUrl: values.websiteUrl.trim() || undefined
    });
  };

  const validate = (values: FormValues) => {
    const errors: Partial<FormValues> = {};

    // Check for duplicate Branch ID (if provided)
    if (values.id.trim() && existingBranches.some(branch => branch.id === values.id.trim())) {
      errors.id = "A branch with this ID already exists";
    }

    // Check for duplicate XTRF ID
    if (values.xtrfId && existingBranches.some(branch => branch.xtrfId === Number(values.xtrfId))) {
      errors.xtrfId = "A branch with this XTRF ID already exists";
    }

    // Check for duplicate name
    if (values.name && existingBranches.some(branch => branch.name === values.name)) {
      errors.name = "A branch with this name already exists";
    }

    return errors;
  };

  return (
    <Drawer open={true} onClose={onClose} anchor="right" PaperProps={{ sx: { width: 500 } }}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validate={validate}
      >
        {({ isSubmitting, isValid, dirty }) => {
          return (
            <Form>
              <DrawerHeader title="Add Branch" onClose={onClose} />
              <DrawerBody>
                <Stack spacing={3}>
                  <TextInputField
                    name="id"
                    label="Branch ID"
                    required
                    fullWidth
                    placeholder="Enter a unique branch identifier"
                    helperText="Required: Provide a unique identifier for this branch"
                  />
                  <TextInputField name="name" label="Branch Name" required fullWidth autoFocus />
                  <TextInputField name="xtrfId" label="XTRF ID" required fullWidth type="number" />
                  <SelectOneField name="currency" label="Currency" required fullWidth options={currencyOptions} />
                  <SelectOneField
                    name="xtrfServiceId"
                    label="Default XTRF Service"
                    fullWidth
                    options={xtrfServiceOptions}
                  />
                  <TextInputField
                    name="supportEmail"
                    label="Customer Support Email Address"
                    fullWidth
                    type="email"
                    placeholder="Enter customer support email address"
                  />
                  <TextInputField name="phoneNumber" label="Phone Number" fullWidth placeholder="Enter phone number" />
                  <TextInputField
                    name="websiteUrl"
                    label="Website URL"
                    fullWidth
                    type="url"
                    placeholder="Enter website URL (e.g., https://example.com)"
                  />
                </Stack>
              </DrawerBody>
              <DrawerActions>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  isLoading={isSubmitting}
                  disabled={!isValid || !dirty}
                >
                  Save
                </LoadingButton>
                <Button onClick={onClose}>Cancel</Button>
              </DrawerActions>
            </Form>
          );
        }}
      </Formik>
    </Drawer>
  );
};

export { AddBranchPanel };
